<script setup>
import AppLogo from '@/components/app/AppLogo.vue'
import AppMenu from '@/components/app/AppMenu.vue'
import AppThemeToggle from '@/components/app/AppThemeToggle.vue'
import DropdownMenuItem from '@/components/shared/dropdown/DropdownMenuItem.vue'
import WidthLimiter from '@/components/shared/page/WidthLimiter.vue'
import ActionButton from '@/components/shared/buttons/ActionButton.vue'
import {openUrl} from '@/helpers/url.js'
import {useTeams} from '@/composables/use-teams.js'
import {useCurrentOrganization} from '@/composables/use-current-organization.js'
import {useBreakpoints} from '@/composables/use-breakpoints.js'
import {useNavigation} from '@/composables/use-navigation.js'
import {ref, computed} from 'vue'

//Composables
const {isInTeams} = useTeams()
const {goToHome, goToEnroll} = useNavigation()
const {isMobile, isDesktop} = useBreakpoints()
const {
  showMessengerEnrollMenuItem,
  showMessengerHelpMenuItem,
  showMessengerFeedbackMenuItem,
  messengerFeedbackUrl,
} = useCurrentOrganization()

//Buttons to show
const showEnrollButton = computed(() => showMessengerEnrollMenuItem.value)
const canShowFeedbackAsButton = computed(() => isInTeams.value && !showEnrollButton.value)
const showFeedbackButton = computed(() => showMessengerFeedbackMenuItem.value && messengerFeedbackUrl.value && (isDesktop.value || canShowFeedbackAsButton.value))

//Menu items to show
const showFeedbackMenuItem = computed(() => showMessengerFeedbackMenuItem.value && messengerFeedbackUrl.value && isMobile.value && !canShowFeedbackAsButton.value)
const showHelpMenuItem = computed(() => showMessengerHelpMenuItem.value)
const showThemeToggleMenuItem = computed(() => !isInTeams.value)

//Check if menu is open
const isMenuOpen = ref(false)
const setMenuOpen = value => isMenuOpen.value = value
</script>

<template>
  <div :class="$style.wrapper">
    <WidthLimiter :class="$style.inner">
      <div :class="$style.logoWrapper">
        <AppLogo
          v-if="!isInTeams"
          is-clickable
          @click="goToHome()"
        />
      </div>
      <div :class="$style.actions">
        <ActionButton
          v-if="showEnrollButton"
          :class="{
            [$style.actionButton]: true,
            [$style.menuOpen]: isMenuOpen,
          }"
          label="Enroll"
          type="secondary"
          icon="add"
          is-slim
          @click="goToEnroll()"
        />
        <ActionButton
          v-if="showFeedbackButton"
          :class="{
            [$style.actionButton]: true,
            [$style.menuOpen]: isMenuOpen,
          }"
          label="Submit feedback"
          type="secondary"
          icon="chat"
          is-icon-outlined
          is-slim
          @click="openUrl(messengerFeedbackUrl, null, true)"
        />
        <AppMenu
          @open="setMenuOpen(true)"
          @close="setMenuOpen(false)"
        >
          <slot name="actions" />
          <DropdownMenuItem
            v-if="showFeedbackMenuItem"
            label="Submit feedback"
            icon="chat"
            @click="openUrl(messengerFeedbackUrl, null, true)"
          />
          <DropdownMenuItem
            v-if="showHelpMenuItem"
            label="Help"
            icon="contact_support"
            @click="openUrl('https://help.arist.co/category/117-support-for-learners', null, true)"
          />
          <AppThemeToggle v-if="showThemeToggleMenuItem" />
        </AppMenu>
      </div>
    </WidthLimiter>
  </div>
</template>

<style module lang="scss">
.wrapper {
  background: var(--bg-page-element);
  border-bottom: 1px solid var(--color-borders);
  height: var(--topbar-height);
  flex: 0 0 var(--topbar-height);
}
.inner {
  height: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 0.5rem;
}
.logoWrapper {
  flex: 1;
}
.actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Hide any action buttons in top bar when the menu is open */
.actionButton {
  &.menuOpen {
    @media (max-width: 767px) {
      display: none;
    }
  }
}
</style>
